import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Button, Table, Tag, Modal, Form, Input, InputNumber, Select, Switch,
  message, Popconfirm, Space, Typography, Empty, Avatar, Tooltip, Alert,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, TeamOutlined, WhatsAppOutlined, MailOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import api from '../services/api';

const { Text, Title } = Typography;

const POSITION_OPTIONS = [
  { value: 'goleiro', label: 'Goleiro' },
  { value: 'zagueiro', label: 'Zagueiro' },
  { value: 'lateral', label: 'Lateral' },
  { value: 'volante', label: 'Volante' },
  { value: 'meia', label: 'Meia' },
  { value: 'atacante', label: 'Atacante' },
  { value: 'linha', label: 'Linha (qualquer)' },
];
const FOOT_OPTIONS = [
  { value: 'direito', label: 'Direito' },
  { value: 'esquerdo', label: 'Esquerdo' },
  { value: 'ambos', label: 'Ambos' },
];

// Elenco visto do painel admin. Descobre o time comunidade automaticamente
// pelo customer_id do organizador logado (backend usa authenticateTeam).
export default function AdminCommunityRoster() {
  const [data, setData] = useState({ is_pro: false, items: [], no_team: false });
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/community/admin-roster');
      setData(r.data);
    } catch (e) {
      message.error(e.response?.data?.message || 'Erro ao carregar elenco');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ add_to_futezap: data.is_pro });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      name: row.name, phone: row.phone, email: row.email,
      position_pref: row.position_pref, level: row.level, foot: row.foot,
      altura_cm: row.altura_cm, peso_kg: row.peso_kg,
      jersey_number: row.jersey_number, notes: row.notes,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing?.cp_id) {
        const { phone, add_to_futezap, ...body } = values;
        await api.put(`/community/admin-roster/${editing.cp_id}`, body);
        message.success('Jogador atualizado');
      } else {
        await api.post('/community/admin-roster', values);
        message.success('Jogador adicionado ao elenco');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleRemove = async (item, alsoFutezap) => {
    try {
      const q = alsoFutezap ? '?also_futezap=1' : '';
      await api.delete(`/community/admin-roster/${item.cp_id}${q}`);
      message.success('Removido do elenco');
      load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Erro ao remover');
    }
  };

  const handleImportFutezap = () => {
    Modal.confirm({
      title: 'Importar elenco do Futezap?',
      content: 'Vou trazer todos os jogadores ativos do time Futezap pro elenco da comunidade. Emails ficam em branco — organizador ou o próprio jogador completa depois.',
      okText: 'Importar',
      onOk: async () => {
        try {
          const r = await api.post('/community/admin-roster/import-futezap');
          message.success(`Importados: ${r.data.linked} adicionados ao elenco (${r.data.created} novos na comunidade${r.data.skipped ? `, ${r.data.skipped} pulados por telefone inválido` : ''})`);
          load();
        } catch (e) {
          message.error(e.response?.data?.message || 'Erro ao importar');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Jogador', key: 'name',
      render: (_, r) => (
        <Space>
          <Avatar src={r.profile_photo} icon={<TeamOutlined />}
            style={{ background: r.profile_photo ? undefined : '#128C7E' }} />
          <div>
            <div style={{ fontWeight: 600 }}>{r.name}</div>
            {r.jersey_number && <Tag color="gold">#{r.jersey_number}</Tag>}
          </div>
        </Space>
      ),
    },
    {
      title: 'Contato', key: 'contact',
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          {r.phone && <div><WhatsAppOutlined /> {r.phone}</div>}
          {r.email
            ? <div><MailOutlined /> {r.email}</div>
            : r.cp_id && <div><Tag color="orange" style={{ fontSize: 10 }}>sem email</Tag></div>}
        </div>
      ),
    },
    {
      title: 'Posição / Nível', key: 'pos',
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          {r.position_pref && <Tag>{r.position_pref}</Tag>}
          {r.level && <Tag color="blue"><TrophyOutlined /> nvl {r.level}</Tag>}
          {r.foot && <Tag>{r.foot}</Tag>}
        </div>
      ),
    },
    {
      title: 'Vínculo', key: 'source', width: 150,
      render: (_, r) => {
        if (r.source === 'ambos') return <Tag color="green">Comunidade + Futezap</Tag>;
        if (r.source === 'comunidade') return <Tag color="cyan">Comunidade</Tag>;
        return <Tag color="orange">Só Futezap</Tag>;
      },
    },
    {
      title: '', key: 'actions', width: 140,
      render: (_, r) => r.cp_id ? (
        <Space size={4}>
          <Button size="small" onClick={() => openEdit(r)}>Editar</Button>
          <Popconfirm
            title={data.is_pro && r.futezap_player_id
              ? 'Remover só do elenco ou também do Futezap?'
              : 'Remover do elenco?'}
            okText={data.is_pro && r.futezap_player_id ? 'Só elenco' : 'Sim'}
            cancelText={data.is_pro && r.futezap_player_id ? 'Elenco + Futezap' : 'Não'}
            onConfirm={() => handleRemove(r, false)}
            onCancel={data.is_pro && r.futezap_player_id ? () => handleRemove(r, true) : undefined}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ) : (
        <Tooltip title="Vem só de players do Futezap. Use 'Importar do Futezap' pra trazer pro elenco comunidade.">
          <Tag color="default">Só Futezap</Tag>
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Elenco da comunidade</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {data.is_pro
              ? 'Time Futezap Pro ativo — jogador cadastrado aqui pode entrar direto nas convocações'
              : 'Time só-comunidade — jogadores ficam no elenco pra amistosos/notícias'}
          </Text>
        </div>
        <Space wrap>
          {data.is_pro && (
            <Button onClick={handleImportFutezap}>Importar do Futezap</Button>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} disabled={data.no_team}>
            Adicionar jogador
          </Button>
        </Space>
      </div>

      {data.no_team && (
        <Alert style={{ marginBottom: 12 }} type="warning" showIcon
          message="Sem time comunidade cadastrado ainda"
          description="Antes de montar elenco, crie o time comunidade em Comunidade → Perfil do time." />
      )}

      <Card size="small">
        <Table
          size="small"
          rowKey={r => r.cp_id || `fzp_${r.futezap_player_id}`}
          loading={loading}
          dataSource={data.items}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          locale={{ emptyText: <Empty description="Sem elenco cadastrado ainda" /> }}
        />
      </Card>

      <Modal
        title={editing ? 'Editar jogador' : 'Novo jogador'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        okText={editing ? 'Salvar' : 'Cadastrar'}
        confirmLoading={saving}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Nome" rules={[{ required: true, min: 2 }]}>
            <Input placeholder="Nome do boleiro" />
          </Form.Item>
          <Form.Item name="phone" label="Whatsapp (DDD + número)"
            rules={editing ? [] : [{ required: true, pattern: /^\d{10,11}$/, message: '10 ou 11 dígitos' }]}
            extra={editing ? 'Não editável (chave global)' : 'Chave global do jogador'}>
            <Input placeholder="11987654321" maxLength={11} disabled={!!editing} />
          </Form.Item>
          <Form.Item name="email" label="Email"
            rules={[{ required: true, type: 'email', message: 'Email válido — usamos pra notícias e jogos' }]}
            extra="Recebe notícias do time, agenda de jogos, convites">
            <Input placeholder="email@exemplo.com" />
          </Form.Item>
          <Space size={8} style={{ display: 'flex' }}>
            <Form.Item name="position_pref" label="Posição" style={{ flex: 1 }}>
              <Select options={POSITION_OPTIONS} allowClear placeholder="—" />
            </Form.Item>
            <Form.Item name="foot" label="Pé" style={{ width: 110 }}>
              <Select options={FOOT_OPTIONS} allowClear placeholder="—" />
            </Form.Item>
            <Form.Item name="level" label="Nível 1-10" style={{ width: 110 }}>
              <InputNumber min={1} max={10} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Space size={8} style={{ display: 'flex' }}>
            <Form.Item name="jersey_number" label="Camisa" style={{ width: 100 }}>
              <InputNumber min={1} max={99} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="altura_cm" label="Altura (cm)" style={{ width: 130 }}>
              <InputNumber min={100} max={230} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="peso_kg" label="Peso (kg)" style={{ width: 120 }}>
              <InputNumber min={30} max={200} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notas (opcional)">
            <Input.TextArea rows={2} placeholder="Observações internas do organizador" />
          </Form.Item>
          {data.is_pro && !editing && (
            <Form.Item name="add_to_futezap" label="Incluir nas convocações do Futezap"
              valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
